import sliderCss from 'blaze-slider/dist/blaze.css?url'

import {PropsWithChildren, Suspense, useCallback, useMemo, useState} from 'react'
import {HiArrowRight, HiMiniArrowRight} from 'react-icons/hi2'
import {createFileRoute, Link} from '@tanstack/react-router'
import {zodValidator} from '@tanstack/zod-adapter'
import {useTranslations} from 'use-intl'
import {compact} from 'lodash-es'
import Color from 'color'
import clsx from 'clsx'
import {z} from 'zod'
import {
    ProductSuggestions,
    ProductSuggestionsSkeletonLoader,
} from '~/components/product-details/ProductSuggestions'
import {filterProductVariants, getAppearances, getDefaultModelImageSrc} from '~/utils/product'
import AddToBasketButtonBox from '~/components/product-details/AddToBasketButtonBox'
import ProductTypeDialog from '~/components/product-details/ProductTypeDialog'
import ReviewsDialog from '~/components/product-details/ReviewsDialog'
import DesignDialog from '~/components/product-details/DesignDialog'
import SizeDialog from '~/components/product-details/SizeDialog'
import AppearanceSelector from '~/components/AppearanceSelector'
import ProductImageSlider from '~/components/ProductImageSlider'
import Breadcrumbs from '~/components/common/Breadcrumbs'
import useLocaleParams from '~/hooks/useLocaleParams'
import SizeSelector from '~/components/SizeSelector'
import {getProduct} from '~/client/fn/product'
import Price from '~/components/common/Price'
import Card from '~/components/common/Card'
import {buildBreadcrumbJsonLd, buildProductJsonLd, jsonLdScript} from '~/utils/jsonLd'
import seo from '~/utils/seo'

const productSearchSchema = z.object({
    appearanceId: z.union([z.string().regex(/[0-9]+/), z.number(), z.null()]).optional(),
})

export const Route = createFileRoute('/$lang/_layout/products/$productId')({
    validateSearch: zodValidator(productSearchSchema),
    component: RouteComponent,
    loader: async ({params, context}) => {
        const product = await getProduct({
            data: {locale: context.locale, productId: params.productId},
        })

        if (!product) {
            throw new Error('Product data not found.')
        }

        return {product}
    },
    shouldReload: false,
    head: ({loaderData, match, params}) => {
        const product = loaderData?.product
        const lang = params.lang

        if (!product) {
            return {
                links: [
                    {
                        rel: 'stylesheet',
                        href: sliderCss,
                    },
                    {
                        rel: 'preload',
                        href: sliderCss,
                        as: 'style',
                    },
                ],
            }
        }

        const productImageSrc = getDefaultModelImageSrc(
            product,
            product.images,
            match.search.appearanceId ?? undefined
        )

        const canonicalUrl = `https://gravity-shirts.com/${lang}/products/${product.id}`
        const hasStock = product.variants?.some((v) => (v?.stockQuantity ?? 0) > 0)

        const metaTags = seo({
            title: compact([product.name, product.type?.name]).join(' | '),
            description: product.description ?? undefined,
            keywords: product.tags?.join(', '),
            ogUrl: canonicalUrl,
            locale: lang,
            ogType: 'product',
            image: {
                src: `${productImageSrc}&width=1200&height=630&backgroundColor=1c1d2c`,
                width: 1200,
                height: 630,
            },
        })

        const productJsonLd = buildProductJsonLd({
            name: product.name ?? '',
            description: product.description ?? undefined,
            image: productImageSrc,
            sku: product.variants?.[0]?.sku ?? undefined,
            brand: product.type?.brand ?? undefined,
            price: product.price?.vatIncluded ?? undefined,
            availability: hasStock ? 'InStock' : 'OutOfStock',
            url: canonicalUrl,
        })

        const breadcrumbJsonLd = buildBreadcrumbJsonLd([
            {name: 'Home', url: `https://gravity-shirts.com/${lang}`},
            {name: 'Products', url: `https://gravity-shirts.com/${lang}/products`},
            {name: product.name ?? '', url: canonicalUrl},
        ])

        return {
            meta: metaTags.length > 0 ? metaTags : undefined,
            links: [
                {
                    rel: 'stylesheet',
                    href: sliderCss,
                },
                {
                    rel: 'preload',
                    href: sliderCss,
                    as: 'style',
                },
                {
                    rel: 'preload',
                    href: productImageSrc,
                    as: 'image',
                },
            ],
            scripts: [jsonLdScript(productJsonLd), jsonLdScript(breadcrumbJsonLd)],
        }
    },
})

type Dialog = 'product' | 'size' | 'design' | 'reviews'

function RouteComponent() {
    const params = useLocaleParams()
    const t = useTranslations('productDetails')
    const navigate = Route.useNavigate()
    const search = Route.useSearch()

    const {product} = Route.useLoaderData()

    const [activeDialog, setActiveDialog] = useState<Dialog | undefined>(undefined)
    const closeDialog = useCallback(() => setActiveDialog(undefined), [])

    const variants = compact(product.variants)
    const appearances = useMemo(() => getAppearances(variants), [variants])

    const defaultAppearance = useMemo(() => {
        const appearanceId = product.defaultAppearanceId

        if (!appearanceId) {
            return appearances[0]
        }

        return appearances.find((appearance) => appearance.id === appearanceId) ?? appearances[0]
    }, [appearances, product.defaultAppearanceId])

    const selectedAppearance = useMemo(() => {
        if (!search.appearanceId) {
            return defaultAppearance
        }

        return (
            appearances.find((appearance) => appearance.id === search.appearanceId) ??
            defaultAppearance
        )
    }, [search.appearanceId, defaultAppearance, appearances])

    const variantsByAppearance = useMemo(() => {
        if (!selectedAppearance.id) {
            return []
        }

        return (
            filterProductVariants(variants, {
                appearanceId: selectedAppearance?.id ?? undefined,
            }) ?? []
        )
    }, [variants, selectedAppearance.id])

    const [selectedSizeId, setSelectedSizeId] = useState<string | undefined>(
        // preselect possible one-size
        variantsByAppearance.length === 1
            ? (variantsByAppearance[0].size?.id ?? undefined)
            : undefined
    )

    const selectedVariant = useMemo(() => {
        if (!selectedSizeId || !selectedAppearance.id) {
            return undefined
        }

        return filterProductVariants(variants, {
            sizeId: selectedSizeId,
            appearanceId: selectedAppearance.id,
        })[0]
    }, [selectedSizeId, selectedAppearance.id, variants])

    const isCompletelyOutOfStock = useMemo<boolean>(() => {
        const hasVariantWithStock = variants.some((variant) => (variant?.stockQuantity ?? 0) > 0)
        return !hasVariantWithStock
    }, [variants])

    const isDarkAppearance = useMemo(() => {
        const appearanceColor = Color(selectedAppearance.values?.[0] ?? undefined)
        return appearanceColor.isDark()
    }, [selectedAppearance.values])

    return (
        <>
            <div className="px-4 py-6 mx-auto max-w-screen-xl lg:py-12 sm:px-6">
                <Breadcrumbs
                    className="mb-2 md:mb-6 md:mx-4"
                    items={[
                        {name: t('breadcrumbs.home'), to: '/$lang', params},
                        {name: t('breadcrumbs.products'), to: '/$lang/products', params},
                        {name: product.name ?? '', to: '.'},
                    ]}
                />
                <div className="md:grid md:gap-6 md:grid-cols-2">
                    <div>
                        <Card
                            className={clsx(
                                'relative transition-colors overflow-hidden',
                                isDarkAppearance && '!bg-white/10'
                            )}
                        >
                            <ProductImageSlider
                                product={product}
                                selectedAppearanceId={selectedAppearance.id!}
                            />
                        </Card>
                    </div>
                    <div className="my-4">
                        <h1 className="text-2xl font-medium mb-1 truncate">{product.name}</h1>
                        <div className="text-sm md:text-base flex items-center gap-2 overflow-hidden text-gray-300">
                            <p className="truncate">{product.type?.name}</p>
                            <span className="rounded-full size-1 bg-gray-400" aria-hidden="true" />
                            <p className="capitalize flex-[1_0_auto]">{selectedAppearance.name}</p>
                        </div>
                        <p>
                            <Price
                                className="text-lg font-bold whitespace-nowrap"
                                aria-label="Price"
                            >
                                {product.price?.vatIncluded}
                            </Price>
                            <span className="text-xs ml-2">{t('inclVat')}</span>
                        </p>

                        {!!product.description && (
                            <p className="mt-3 text-sm text-stardust/70 line-clamp-3">
                                {product.description}
                            </p>
                        )}

                        <div className="mt-4">
                            <h2 className="text-sm font-medium mb-2">{t('color')}</h2>
                            <AppearanceSelector
                                label={t('selectColor')}
                                variants={variants}
                                selectedAppearanceId={selectedAppearance.id!}
                                onAppearanceSelect={(appearance) => {
                                    void navigate({
                                        replace: true,
                                        resetScroll: false,
                                        search: {
                                            appearanceId: appearance.id,
                                        },
                                    })
                                }}
                            />
                        </div>
                        <div className="mt-6">
                            <div className="flex justify-between items-center mb-2">
                                <h2 className="text-sm font-medium">{t('size')}</h2>
                                <button
                                    className="text-sm text-comet"
                                    onClick={() => setActiveDialog('size')}
                                >
                                    {t('showChart')}
                                </button>
                            </div>
                            <SizeSelector
                                label={t('selectSize')}
                                variants={variants}
                                selectedAppearanceId={selectedAppearance.id!}
                                selectedSizeId={selectedSizeId}
                                onSizeSelect={(size) => setSelectedSizeId(size.id!)}
                            />
                        </div>

                        {isCompletelyOutOfStock ? (
                            <Card
                                variant="desructive"
                                className="sticky bottom-4 mt-6 md:relative md:bottom-0 px-4 py-3 text-center"
                            >
                                {t('notAvailable')}
                            </Card>
                        ) : (
                            <AddToBasketButtonBox
                                className="sticky bottom-4 mt-6 md:relative md:bottom-0"
                                selectedVariant={selectedVariant}
                            />
                        )}
                    </div>
                </div>
                <div className="my-4">
                    <div className="mt-12">
                        <ul className="divide-y divide-white/10 border border-white/10 bg-gray-900/10 backdrop-blur-sm transform-gpu rounded-md shadow-md shadow-gray-600">
                            <ActionListItem onClick={() => setActiveDialog('product')}>
                                {t('actions.productInformation')}
                            </ActionListItem>
                            <ActionListItem onClick={() => setActiveDialog('size')}>
                                {t('actions.sizeChart')}
                            </ActionListItem>
                            <ActionListItem onClick={() => setActiveDialog('design')}>
                                {t('actions.designInformation')}
                            </ActionListItem>
                            <ActionListItem onClick={() => setActiveDialog('reviews')}>
                                {t('actions.reviews')}
                            </ActionListItem>
                        </ul>
                        <ProductTypeDialog
                            open={activeDialog === 'product'}
                            product={product}
                            onClose={closeDialog}
                            selectedAppearance={selectedAppearance}
                        />
                        <SizeDialog
                            open={activeDialog === 'size'}
                            product={product}
                            onClose={closeDialog}
                            selectedSize={selectedVariant?.size ?? undefined}
                        />
                        <DesignDialog
                            open={activeDialog === 'design'}
                            product={product}
                            onClose={closeDialog}
                        />
                        <ReviewsDialog
                            open={activeDialog === 'reviews'}
                            product={product}
                            onClose={closeDialog}
                        />
                    </div>

                    <div className="mt-8">
                        <div className="flex flex-row justify-between gap-2 items-center flex-wrap mb-2">
                            <h2 className="font-bold">{t('productSuggestions.title')}</h2>
                            <Link
                                to="/$lang/products"
                                params={params}
                                search={{design: product.ideaId ?? undefined}}
                                className="flex flex-row items-center gap-1 font-medium text-comet text-sm md:text-base"
                            >
                                <span>{t('productSuggestions.showAll')}</span>
                                <HiMiniArrowRight className="size-4 md:size-5" />
                            </Link>
                        </div>

                        <Suspense fallback={<ProductSuggestionsSkeletonLoader />}>
                            <ProductSuggestions
                                productId={product.id!}
                                appearanceId={selectedAppearance.id!}
                            />
                        </Suspense>
                    </div>
                </div>
            </div>
        </>
    )
}

type ActionListItemProps = PropsWithChildren<{
    onClick: () => void
}>

const ActionListItem = ({children, onClick}: ActionListItemProps) => {
    return (
        <li>
            <button
                onClick={onClick}
                className="flex flex-row justify-between items-center w-full px-4 py-6"
            >
                <span className="font-bold">{children}</span>
                <HiArrowRight className="size-6" />
            </button>
        </li>
    )
}
